import { Controller } from "@hotwired/stimulus"
import dragula from "dragula"

// Workflow Canvas Controller
// Manages the visual workflow builder for automations
export default class extends Controller {
  static targets = [
    "canvas",
    "nodesContainer",
    "conditionsSection",
    "actionsSection",
    "endNode",
    "triggerSelect",
    "triggerConfig",
    "enricherConfig",
    "webhookConfig",
    "fromStatus",
    "toStatus",
    "conditionTemplate",
    "actionTemplate",
    "nodePalette",
    "dropZone",
    "scheduleConfig",
    "scheduleType",
    "scheduleTimeContainer",
    "scheduleDayOfWeekContainer",
    "scheduleDayOfMonthContainer",
    "scheduleIntervalContainer",
    "infoBox",
    "infoTitle",
    "infoDescription",
    "infoTips",
    "infoTipsList"
  ]

  static values = {
    triggers: { type: Object, default: {} },
    actions: { type: Object, default: {} }
  }

  connect() {
    this.conditionIndex = this.conditionsSectionTarget.querySelectorAll('.condition-node').length
    this.actionIndex = this.actionsSectionTarget.querySelectorAll('.action-node').length
    this.updateEndNodeVisibility()
    this.initializeTriggerConfig()
    this.initDragAndDrop()
    this.initPaletteDrag()
    this.updateInfoBoxFromTrigger()
  }

  disconnect() {
    if (this.conditionsDrake) {
      this.conditionsDrake.destroy()
    }
    if (this.actionsDrake) {
      this.actionsDrake.destroy()
    }
  }

  // Initialize drag-and-drop for reordering existing nodes
  initDragAndDrop() {
    // Conditions drag-and-drop for reordering
    this.conditionsDrake = dragula([this.conditionsSectionTarget], {
      moves: (el, container, handle) => {
        // Only allow dragging via the handle (icon with cursor-move)
        return handle.closest('.cursor-move') !== null
      },
      direction: 'vertical'
    })

    // Actions drag-and-drop for reordering
    this.actionsDrake = dragula([this.actionsSectionTarget], {
      moves: (el, container, handle) => {
        // Only allow dragging via the handle (icon with cursor-move)
        return handle.closest('.cursor-move') !== null
      },
      direction: 'vertical'
    })
  }

  // Initialize drag from palette to canvas using HTML5 drag and drop
  initPaletteDrag() {
    if (!this.hasNodePaletteTarget || !this.hasDropZoneTarget) return

    // Set up draggable items in palette
    const paletteItems = this.nodePaletteTarget.querySelectorAll('.palette-item')
    paletteItems.forEach(item => {
      item.setAttribute('draggable', 'true')

      item.addEventListener('dragstart', (e) => {
        e.dataTransfer.setData('text/plain', item.dataset.nodeType)
        e.dataTransfer.effectAllowed = 'copy'
        item.classList.add('opacity-50')
        this.dropZoneTarget.classList.add('drop-zone-active')
      })

      item.addEventListener('dragend', () => {
        item.classList.remove('opacity-50')
        this.dropZoneTarget.classList.remove('drop-zone-active')
      })
    })

    // Set up drop zone
    this.dropZoneTarget.addEventListener('dragover', (e) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
      this.dropZoneTarget.classList.add('drop-zone-active')
    })

    this.dropZoneTarget.addEventListener('dragleave', (e) => {
      // Only remove if leaving the drop zone entirely
      if (!this.dropZoneTarget.contains(e.relatedTarget)) {
        this.dropZoneTarget.classList.remove('drop-zone-active')
      }
    })

    this.dropZoneTarget.addEventListener('drop', (e) => {
      e.preventDefault()
      this.dropZoneTarget.classList.remove('drop-zone-active')

      const nodeType = e.dataTransfer.getData('text/plain')
      if (nodeType === 'condition') {
        this.addCondition()
      } else if (nodeType === 'action') {
        this.addAction()
      }
    })
  }

  // Trigger type change handler
  triggerTypeChanged(event) {
    const triggerType = event.target.value

    // Hide all config panels first
    this.triggerConfigTarget.classList.add('hidden')
    if (this.hasScheduleConfigTarget) {
      this.scheduleConfigTarget.classList.add('hidden')
    }
    if (this.hasEnricherConfigTarget) {
      this.enricherConfigTarget.classList.add('hidden')
    }
    if (this.hasWebhookConfigTarget) {
      this.webhookConfigTarget.classList.add('hidden')
    }

    // Show relevant config based on type
    if (triggerType === 'company_status_changed') {
      this.triggerConfigTarget.classList.remove('hidden')
    } else if (triggerType === 'scheduled') {
      if (this.hasScheduleConfigTarget) {
        this.scheduleConfigTarget.classList.remove('hidden')
      }
    } else if (triggerType === 'enrichment_completed') {
      if (this.hasEnricherConfigTarget) {
        this.enricherConfigTarget.classList.remove('hidden')
      }
    } else if (triggerType === 'webhook_received') {
      if (this.hasWebhookConfigTarget) {
        this.webhookConfigTarget.classList.remove('hidden')
      }
    }

    // Update info box with trigger info
    this.updateInfoBoxFromTrigger()
  }

  // Schedule type change handler
  scheduleTypeChanged(event) {
    const scheduleType = event.target.value

    // Show/hide time container (hidden for interval)
    if (this.hasScheduleTimeContainerTarget) {
      if (scheduleType === 'interval' || !scheduleType) {
        this.scheduleTimeContainerTarget.classList.add('hidden')
      } else {
        this.scheduleTimeContainerTarget.classList.remove('hidden')
      }
    }

    // Show/hide day of week (only for weekly)
    if (this.hasScheduleDayOfWeekContainerTarget) {
      if (scheduleType === 'weekly') {
        this.scheduleDayOfWeekContainerTarget.classList.remove('hidden')
      } else {
        this.scheduleDayOfWeekContainerTarget.classList.add('hidden')
      }
    }

    // Show/hide day of month (only for monthly)
    if (this.hasScheduleDayOfMonthContainerTarget) {
      if (scheduleType === 'monthly') {
        this.scheduleDayOfMonthContainerTarget.classList.remove('hidden')
      } else {
        this.scheduleDayOfMonthContainerTarget.classList.add('hidden')
      }
    }

    // Show/hide interval hours (only for interval)
    if (this.hasScheduleIntervalContainerTarget) {
      if (scheduleType === 'interval') {
        this.scheduleIntervalContainerTarget.classList.remove('hidden')
      } else {
        this.scheduleIntervalContainerTarget.classList.add('hidden')
      }
    }
  }

  initializeTriggerConfig() {
    const currentTrigger = this.triggerSelectTarget.value
    if (currentTrigger === 'company_status_changed') {
      this.triggerConfigTarget.classList.remove('hidden')
    } else if (currentTrigger === 'scheduled' && this.hasScheduleConfigTarget) {
      this.scheduleConfigTarget.classList.remove('hidden')
    } else if (currentTrigger === 'enrichment_completed' && this.hasEnricherConfigTarget) {
      this.enricherConfigTarget.classList.remove('hidden')
    } else if (currentTrigger === 'webhook_received' && this.hasWebhookConfigTarget) {
      this.webhookConfigTarget.classList.remove('hidden')
    }
  }

  // Add a new condition node
  addCondition() {
    const template = this.conditionTemplateTarget.content.cloneNode(true)
    const node = template.querySelector('.condition-node')

    // Update index in name attributes
    node.querySelectorAll('[name*="INDEX"]').forEach(input => {
      input.name = input.name.replace('INDEX', this.conditionIndex)
    })
    node.setAttribute('data-node-index', this.conditionIndex)

    this.conditionsSectionTarget.appendChild(node)
    this.conditionIndex++
    this.updateEndNodeVisibility()

    // Animate in
    node.style.opacity = '0'
    node.style.transform = 'translateX(-20px)'
    requestAnimationFrame(() => {
      node.style.transition = 'all 0.3s ease'
      node.style.opacity = '1'
      node.style.transform = 'translateX(0)'
    })
  }

  // Remove a condition node
  removeCondition(event) {
    const node = event.target.closest('.condition-node')
    if (node) {
      node.style.transition = 'all 0.2s ease'
      node.style.opacity = '0'
      node.style.transform = 'translateX(-20px)'
      setTimeout(() => {
        node.remove()
        this.updateEndNodeVisibility()
      }, 200)
    }
  }

  // Add a new action node
  addAction() {
    const template = this.actionTemplateTarget.content.cloneNode(true)
    const node = template.querySelector('.action-node')

    // Update index in name attributes
    node.querySelectorAll('[name*="INDEX"]').forEach(input => {
      input.name = input.name.replace('INDEX', this.actionIndex)
    })
    node.setAttribute('data-node-index', this.actionIndex)

    this.actionsSectionTarget.appendChild(node)
    this.actionIndex++
    this.updateEndNodeVisibility()

    // Animate in
    node.style.opacity = '0'
    node.style.transform = 'translateX(-20px)'
    requestAnimationFrame(() => {
      node.style.transition = 'all 0.3s ease'
      node.style.opacity = '1'
      node.style.transform = 'translateX(0)'
    })
  }

  // Remove an action node
  removeAction(event) {
    const node = event.target.closest('.action-node')
    if (node) {
      node.style.transition = 'all 0.2s ease'
      node.style.opacity = '0'
      node.style.transform = 'translateX(-20px)'
      setTimeout(() => {
        node.remove()
        this.updateEndNodeVisibility()
      }, 200)
    }
  }

  // Update visibility of end node placeholder
  updateEndNodeVisibility() {
    const hasConditions = this.conditionsSectionTarget.querySelectorAll('.condition-node').length > 0
    const hasActions = this.actionsSectionTarget.querySelectorAll('.action-node').length > 0

    if (hasConditions || hasActions) {
      this.endNodeTarget.classList.add('hidden')
    } else {
      this.endNodeTarget.classList.remove('hidden')
    }
  }

  // Pre-submit handler to clean up form data
  beforeSubmit(event) {
    // Reindex conditions to ensure sequential array
    this.conditionsSectionTarget.querySelectorAll('.condition-node').forEach((node, index) => {
      node.querySelectorAll('[name*="conditions"]').forEach(input => {
        input.name = input.name.replace(/conditions\]\[\d*\]/, `conditions][${index}]`)
      })
    })

    // Reindex actions to ensure sequential array
    this.actionsSectionTarget.querySelectorAll('.action-node').forEach((node, index) => {
      node.querySelectorAll('[name*="actions"]').forEach(input => {
        input.name = input.name.replace(/actions\]\[\d*\]/, `actions][${index}]`)
      })
    })
  }

  // Update info box when trigger changes
  updateInfoBoxFromTrigger() {
    if (!this.hasInfoBoxTarget) return

    const triggerType = this.triggerSelectTarget.value
    if (!triggerType) {
      this.showDefaultInfo()
      return
    }

    const triggerInfo = this.triggersValue[triggerType]
    if (triggerInfo) {
      this.updateInfoBox("Trigger", triggerInfo.label, triggerInfo.description, this.getTriggerTips(triggerType))
    } else {
      this.showDefaultInfo()
    }
  }

  // Update info box when action changes
  updateInfoBoxFromAction(event) {
    if (!this.hasInfoBoxTarget) return

    const actionType = event.target.value
    if (!actionType) {
      this.updateInfoBoxFromTrigger()
      return
    }

    const actionInfo = this.actionsValue[actionType]
    if (actionInfo) {
      this.updateInfoBox("Action", actionInfo.label, actionInfo.description, this.getActionTips(actionType))
    }
  }

  // Show default getting started info
  showDefaultInfo() {
    if (!this.hasInfoTitleTarget) return

    this.infoTitleTarget.textContent = "Getting Started"
    this.infoDescriptionTarget.textContent = "Select a trigger to start building your automation. Triggers define when the automation runs, and actions define what happens."
    if (this.hasInfoTipsTarget) {
      this.infoTipsTarget.classList.add('hidden')
    }
  }

  // Update the info box with new content
  updateInfoBox(type, title, description, tips = []) {
    if (!this.hasInfoTitleTarget) return

    this.infoTitleTarget.textContent = `${type}: ${title}`
    this.infoDescriptionTarget.textContent = description

    if (this.hasInfoTipsTarget && this.hasInfoTipsListTarget) {
      if (tips.length > 0) {
        this.infoTipsTarget.classList.remove('hidden')
        this.infoTipsListTarget.innerHTML = tips.map(tip => `<li>${tip}</li>`).join('')
      } else {
        this.infoTipsTarget.classList.add('hidden')
      }
    }
  }

  // Get tips for specific trigger types
  getTriggerTips(triggerType) {
    const tips = {
      'company_created': [
        'Fires immediately when a company is added',
        'Works with manual creation and imports',
        'Use conditions to filter by company attributes'
      ],
      'contact_created': [
        'Fires when contacts are added to the system',
        'Can trigger on both manual and automated contact creation',
        'Combine with "Add to board" action to track new contacts'
      ],
      'company_status_changed': [
        'Configure "From" and "To" status to be specific',
        'Leave "From" empty to trigger on any status change to target',
        'Great for moving leads through your pipeline'
      ],
      'enrichment_completed': [
        'Fires after enrichment data is fetched',
        'Use to trigger follow-up actions like AI summaries',
        'Check enrichment results with conditions'
      ],
      'scheduled': [
        'Runs automatically at the configured interval',
        'Choose daily, weekly, monthly, or custom interval',
        'Great for recurring tasks like lead discovery'
      ],
      'search_result_created': [
        'Fires when Lead Discovery finds new leads',
        'Perfect for automating lead qualification',
        'Chain with AI summary and enrichment actions'
      ],
      'webhook_received': [
        'Fires when data is POSTed to your webhook URL',
        'Select the incoming webhook to listen to',
        'Use payload data in conditions and actions'
      ]
    }
    return tips[triggerType] || []
  }

  // Get tips for specific action types
  getActionTips(actionType) {
    const tips = {
      'add_to_board': [
        'Creates a pipeline card on your board',
        'Select target board and starting column',
        'Great for tracking leads through your pipeline'
      ],
      'update_field': [
        'Set any field value on the record',
        'Use to tag, categorize, or update status',
        'Supports text, select, and boolean fields'
      ],
      'convert_to_company': [
        'Creates a company from a Lead Discovery result',
        'Preserves all lead data in the new company',
        'Chain with enrichment for complete data'
      ],
      'generate_ai_summary': [
        'Uses AI to analyze and score the lead',
        'Custom prompts let you focus on specific criteria',
        'Requires Gemini API to be configured'
      ],
      'send_webhook': [
        'Posts data to external services',
        'Supports POST and PUT methods',
        'Include all record data in the payload'
      ],
      'start_research': [
        'Initiates deep research on a company',
        'Gathers comprehensive business intelligence',
        'Results appear in company research tab'
      ],
      'run_website_crawler': [
        'Extracts emails and social links from website',
        'Configure max pages to control depth',
        'Requires lead enrichment to be configured'
      ],
      'lookup_companies_house': [
        'Fetches UK company registration data',
        'Includes directors, filings, and status',
        'Only works for UK registered companies'
      ],
      'run_company_search': [
        'Executes a Google Places search',
        'Configure query and location parameters',
        'Only available for scheduled automations'
      ]
    }
    return tips[actionType] || []
  }
}
